/**
 * Beanz intensity scale — the roast-colour score a roaster assigns to a coffee.
 *
 * This module is deliberately dependency-free (no imports at all) so frontend
 * packages can import its values directly without dragging an AWS SDK or other
 * infrastructure chain across the frontend/backend boundary.
 */

export type IntensityZone = "brightAndLively" | "balancedAndSmooth" | "boldAndDark";

export type IntensityPosition = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface FlavourZoneRange {
  min: IntensityPosition;
  max: IntensityPosition;
}

/** Lightest roast on the scale. */
export const INTENSITY_MIN: IntensityPosition = 1;

/** Darkest roast on the scale. */
export const INTENSITY_MAX: IntensityPosition = 9;

/** Every valid position, lightest first. Handy for rendering the nine blocks. */
export const INTENSITY_POSITIONS: readonly IntensityPosition[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * The nine-step intensity scale, split into three flavour zones.
 *
 *   1  2  3   |   4  5  6   |   7  8  9
 *   bright    |   balanced  |   bold
 *   & lively  |   & smooth  |   & dark
 *
 * Position 1 is the lightest roast (highest Agtron reading); 9 is the darkest
 * (lowest Agtron reading).
 */
export const FLAVOUR_ZONES: Record<IntensityZone, FlavourZoneRange> = {
  brightAndLively: { min: 1, max: 3 },
  balancedAndSmooth: { min: 4, max: 6 },
  boldAndDark: { min: 7, max: 9 },
};

/** Narrows an arbitrary number to a scale position. */
export const isIntensityPosition = (value: unknown): value is IntensityPosition =>
  typeof value === "number" && Number.isInteger(value) && value >= INTENSITY_MIN && value <= INTENSITY_MAX;

/** Narrows an arbitrary value to a known flavour zone key. */
export const isIntensityZone = (value: unknown): value is IntensityZone => typeof value === "string" && Object.hasOwn(FLAVOUR_ZONES, value);

/**
 * The single-word `Intensity_Zone__c` values Salesforce (and the PIM flow
 * downstream of it) expect. Internal zone keys stay as-is — they double as
 * i18n keys and form state — so translate at the payload boundary only.
 */
export const SALESFORCE_INTENSITY_ZONE = {
  brightAndLively: "Bright",
  balancedAndSmooth: "Balanced",
  boldAndDark: "Bold",
} as const satisfies Record<IntensityZone, string>;

export type SalesforceIntensityZone = (typeof SALESFORCE_INTENSITY_ZONE)[IntensityZone];

/** Zone value for a Salesforce payload; `null` when unscored or unrecognised. */
export const toSalesforceIntensityZone = (zone: unknown): SalesforceIntensityZone | null =>
  isIntensityZone(zone) ? SALESFORCE_INTENSITY_ZONE[zone] : null;
